import { apiRequest } from '@/api/client'
import {
  collectionSaleSchema,
  motiveSaleSchema,
  salesPageSchema,
  topicGroupSaleSchema,
  topicSaleSchema,
} from '@/api/sales/schemas'
import type {
  CollectionSale,
  MotiveSale,
  SalesPage,
  SalesQuery,
  TopicGroupSale,
  TopicSale,
} from '@/api/sales/types'

/**
 * Alle vier Ebenen liegen im Modul Analytics, nicht in Platform, und tragen
 * keinen gemeinsamen Controller-Präfix außer dem Modulordner selbst.
 */
const BASE = '/api/analytics'

function query(request: SalesQuery): Record<string, string | number | undefined> {
  return {
    page: request.page,
    pageSize: request.pageSize,
    filter: request.filter,
    orderBy: request.orderBy,
  }
}

export function fetchCollectionSales(
  request: SalesQuery,
  signal?: AbortSignal,
): Promise<SalesPage<CollectionSale>> {
  return apiRequest<SalesPage<CollectionSale>>(`${BASE}/CollectionSales`, {
    schema: salesPageSchema(collectionSaleSchema),
    query: query(request),
    ...(signal ? { signal } : {}),
  })
}

export function fetchMotiveSales(
  request: SalesQuery,
  signal?: AbortSignal,
): Promise<SalesPage<MotiveSale>> {
  return apiRequest<SalesPage<MotiveSale>>(`${BASE}/MotiveSales`, {
    schema: salesPageSchema(motiveSaleSchema),
    query: query(request),
    ...(signal ? { signal } : {}),
  })
}

export function fetchTopicGroupSales(
  request: SalesQuery,
  signal?: AbortSignal,
): Promise<SalesPage<TopicGroupSale>> {
  return apiRequest<SalesPage<TopicGroupSale>>(`${BASE}/TopicGroupSales`, {
    schema: salesPageSchema(topicGroupSaleSchema),
    query: query(request),
    ...(signal ? { signal } : {}),
  })
}

export function fetchTopicSales(
  request: SalesQuery,
  signal?: AbortSignal,
): Promise<SalesPage<TopicSale>> {
  return apiRequest<SalesPage<TopicSale>>(`${BASE}/TopicSales`, {
    schema: salesPageSchema(topicSaleSchema),
    query: query(request),
    ...(signal ? { signal } : {}),
  })
}

/**
 * Baut einen Gridify-Filterausdruck aus einer Freitextsuche über ein
 * Namensfeld.
 *
 * Gridify-Sonderzeichen (`,`, `|`, `(`, `)`, `=`) in der Sucheingabe werden
 * nicht maskiert. Ein Suchbegriff mit einem dieser Zeichen kann deshalb zu
 * einem unerwarteten Filter statt zu einem Volltext-Match führen. Für ein
 * internes Auswertungswerkzeug ist das hinnehmbar, ein Nutzer sieht
 * höchstens zu wenige statt falscher Zeilen.
 */
export function buildNameFilter(field: string, search: string): string | undefined {
  const trimmed = search.trim()
  return trimmed === '' ? undefined : `${field}=*${trimmed}`
}

export function buildOrderBy(field: string, direction: 'asc' | 'desc'): string {
  return `${field} ${direction}`
}
